import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AurButtonConfig, AurButtonIntent, AurButtonSize, AurButtonVariant } from '@shared/models/core-component.models';
import { AurIconComponent } from '../aur-icon/aur-icon.component';
import { AurSpinnerComponent } from '../aur-spinner/aur-spinner.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'aur-button',
  standalone: true,
  imports: [AurIconComponent, AurSpinnerComponent],
  template: `
    <button
      [class]="classes"
      [type]="type"
      [disabled]="disabled || loading"
      [attr.aria-label]="ariaLabel || (iconOnly ? label : null)"
      (click)="clicked.emit($event)"
    >
      @if (loading) {
        <aur-spinner [size]="16" />
      } @else if (leftIcon) {
        <aur-icon [name]="leftIcon" />
      }
      @if (label && !iconOnly) {
        <span class="btn__label">{{ label }}</span>
      }
      @if (!loading && rightIcon && !iconOnly) {
        <aur-icon [name]="rightIcon" />
      }
    </button>
  `,
})
export class AurButtonComponent {
  @Input() label = '';
  @Input() intent: AurButtonIntent = 'brand';
  @Input() variant: AurButtonVariant = 'solid';
  @Input() size?: AurButtonSize;
  @Input() leftIcon?: string;
  @Input() rightIcon?: string;
  @Input() iconOnly = false;
  @Input() block = false;
  @Input() loading = false;
  @Input() disabled = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() ariaLabel?: string;
  @Input() set config(value: AurButtonConfig | null | undefined) {
    if (!value) return;
    Object.assign(this, {
      label: value.label ?? this.label,
      intent: value.intent ?? this.intent,
      variant: value.variant ?? this.variant,
      size: value.size ?? this.size,
      leftIcon: value.leftIcon ?? this.leftIcon,
      rightIcon: value.rightIcon ?? this.rightIcon,
      iconOnly: value.iconOnly ?? this.iconOnly,
      block: value.block ?? this.block,
      loading: value.loading ?? this.loading,
      disabled: value.disabled ?? this.disabled,
      type: value.type ?? this.type,
      ariaLabel: value.ariaLabel ?? this.ariaLabel,
    });
  }
  @Output() clicked = new EventEmitter<MouseEvent>();

  get classes(): string {
    const parts = ['btn', `btn--${this.intent}`, `btn--${this.variant}`];
    if (this.size) parts.push(`btn--s${this.size}`);
    if (this.iconOnly) parts.push('btn--icon');
    if (this.block) parts.push('btn--block');
    if (this.loading) parts.push('is-loading');
    return parts.join(' ');
  }
}
